"use client"

import { useAuthStore } from "@/hooks/use-auth"
import { decodeJWT } from "@/utils/jwt"

export type PermissionAction = "view" | "manage"
export type PermissionModule =
  | "leads"
  | "plans"
  | "roles"
  | "users"
  | "quotes"
  | "pricing"
  | "documents"
  | "activities"
  | "planTypes"
  | "admin-products"
  | "admin-base-rates"
  | "admin-fee-config"
  | "admin-plan-tiers"
  | "admin-loading-config"
  | "admin-config-versions"
  | "admin-optional-benefits"
  | "admin-adjustment-factors"
  | string

export type PermissionsMap = Partial<
  Record<PermissionModule, Partial<Record<PermissionAction, boolean>>>
>

export type RoleName =
  | "super_admin"
  | "admin"
  | "sales"
  | "broker"
  | "agent"
  | "customer"
  | string

interface DecodedToken {
  sub?: string
  email?: string
  role_id?: string
  role_name?: string
  permissions?: Record<string, { view?: boolean; manage?: boolean }>
}

function getPermissionsFromToken(
  token: string | null | undefined
): DecodedToken | null {
  const decoded = decodeJWT(token)
  if (!decoded) return null
  return {
    role_id: decoded.role_id,
    role_name: decoded.role_name,
    permissions: decoded.permissions,
  }
}

export function usePermissions() {
  const tokens = useAuthStore((s) => s.tokens)
  const user = useAuthStore((s) => s.user)

  const decoded = getPermissionsFromToken(tokens?.access_token)

  const permissions: PermissionsMap = decoded?.permissions
    ? Object.fromEntries(
        Object.entries(decoded.permissions).map(([module, actions]) => [
          module,
          {
            view: actions.view ?? false,
            manage: actions.manage ?? false,
          },
        ])
      )
    : {}
  const roleName: RoleName =
    decoded?.role_name ?? (user?.role as RoleName) ?? "customer"

  function hasPermission(
    module: PermissionModule,
    action: PermissionAction
  ): boolean {
    return permissions[module]?.[action] === true
  }

  function canView(module: PermissionModule): boolean {
    return hasPermission(module, "view")
  }

  function canManage(module: PermissionModule): boolean {
    return hasPermission(module, "manage")
  }

  function isRole(...roles: RoleName[]): boolean {
    return roles.includes(roleName)
  }

  function isAdminRole(): boolean {
    const candidates = [
      decoded?.role_name,
      user?.role,
      user?.role_name,
    ].filter(Boolean) as string[]
    return candidates.some((r) => {
      const normalized = r.toLowerCase().replace(/[\s-]/g, "_")
      return normalized === "super_admin" || normalized === "admin"
    })
  }

  const hasSidebar = !!tokens?.access_token

  return {
    permissions,
    roleName,
    hasPermission,
    canView,
    canManage,
    isRole,
    isAdminRole,
    hasSidebar,
  }
}
